import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ListPanel from './ListPanel';
import { SHORTCUT } from '../utils/platform';
import { createDragAutoScroll } from '../lib/dragAutoScroll';

// Grid-mode canvas — tiles live in column/row slots and the layout
// always fills the viewport width. Vertical overflow scrolls per-column
// so a column packed with many tiles doesn't shove the other columns'
// tiles off-screen. Horizontal overflow scrolls the whole canvas only
// when the user explicitly picked a column count that can't fit at
// MIN_COL.
//
// Three drag interactions live here:
//   1) Reorder — grab a tile's header, drop into any slot / new column.
//   2) Column resize — drag the vertical seam between two columns.
//                      Writes board.gridColumnWeights on release.
//   3) Tile resize — drag the horizontal seam between two tiles within
//                    a column. Writes the two tiles' h fields (used as
//                    proportional weight) on release.
//
// Unlike the free canvas, tile positions here are NOT driven by
// list.x/y/w/h. They're derived from list.gridCol + list.gridRow and the
// current viewport size, recomputed on every window resize.

// Must match the free-mode arrange algorithm — lets us move between modes
// without re-tuning thresholds.
// Walter 2026-05-05: dropped from 240 → 192 (~20% narrower) so tiles
// keep shrinking when the browser window gets narrow, and so 4-column
// grids fit into a wider range of viewport widths.
const MIN_COL = 192;
const MIN_TILE_H = 70;
// Width of the "drop here to create a new column" zone at the right edge.
const NEW_COL_ZONE = 28;
// Mouse-move threshold before a mousedown on a tile header is treated as
// a drag (vs a plain click/focus).
const DRAG_THRESHOLD = 5;
// Gap between adjacent tiles — both between columns and between stacked
// tiles within a column. Canvas background shows through the gap, which
// frames each tile without needing a border. Seams and drop indicators
// live in these gaps, so resize affordances never fight tile outlines.
const GAP = 6;
// Outer padding around the whole grid content, matching GAP. Gives the
// edge-tiles (top, bottom, leftmost column, rightmost column) the same
// amount of canvas-bg breathing room as inter-tile gaps — otherwise
// their focus rings would render past the grid-content edge and get
// clipped. Also keeps the visual "frame around each tile" consistent
// whether the tile sits between two other tiles or at the grid edge.
const OUTER_PAD = GAP;
// Seam hit zone straddles the gap by a couple pixels on each side so the
// draggable region is a bit forgiving.
const SEAM_HIT = GAP + 4;

// Sort helper for ordering tiles within a column.
function byGridRow(a, b) {
  const ar = a.gridRow == null ? Number.POSITIVE_INFINITY : a.gridRow;
  const br = b.gridRow == null ? Number.POSITIVE_INFINITY : b.gridRow;
  if (ar !== br) return ar - br;
  return (a.id || '').localeCompare(b.id || '');
}

// Turn a weights array (or absence) into pixel column widths summing to
// totalW and respecting MIN_COL.
//
// Uses the same pin-and-redistribute pattern as computeProportionalHeights:
// any column whose proportional share would fall below MIN_COL gets pinned
// at MIN_COL, and the remaining columns split the leftover space by their
// weights. Iterates until no more pins are needed. This keeps everyone
// visible without forcing horizontal scroll — especially important when a
// column gets added with a small neutral weight (a naive max(MIN,share)
// floor would just inflate the total beyond totalW).
//
// Handles stale weight arrays of the wrong length defensively — pads or
// truncates so a column added via drag-drop doesn't nuke the user's
// previous resize work.
export function computeColumnWidths(weights, n, totalW) {
  let ws;
  if (weights && Array.isArray(weights) && weights.length > 0) {
    ws = weights.slice(0, n).map(w => (typeof w === 'number' && w > 0 ? w : 1));
    while (ws.length < n) ws.push(1);
  } else {
    ws = new Array(n).fill(1);
  }
  // Hard-cap case: if even n × MIN_COL exceeds totalW, we can't fit
  // everyone at minimum — accept horizontal overflow with MIN_COL each.
  if (n * MIN_COL > totalW) return new Array(n).fill(MIN_COL);

  const widths = new Array(n).fill(0);
  const pinned = new Array(n).fill(false);
  for (let iter = 0; iter < n + 1; iter++) {
    let pinnedSpace = 0;
    let freeWeight = 0;
    for (let j = 0; j < n; j++) {
      if (pinned[j]) pinnedSpace += MIN_COL;
      else freeWeight += ws[j];
    }
    const freeSpace = totalW - pinnedSpace;
    if (freeWeight <= 0) break;
    let newPin = -1;
    let smallestShare = Infinity;
    for (let j = 0; j < n; j++) {
      if (pinned[j]) continue;
      const share = (ws[j] / freeWeight) * freeSpace;
      if (share < MIN_COL && share < smallestShare) {
        smallestShare = share;
        newPin = j;
      }
    }
    if (newPin === -1) {
      for (let j = 0; j < n; j++) {
        if (!pinned[j]) {
          widths[j] = Math.max(MIN_COL, Math.floor((ws[j] / freeWeight) * freeSpace));
        } else {
          widths[j] = MIN_COL;
        }
      }
      break;
    }
    pinned[newPin] = true;
  }
  // Fold rounding leftover into the widest non-pinned column so we hit
  // totalW exactly. If everything's pinned, the last column absorbs it.
  const sumNow = widths.reduce((a, b) => a + b, 0);
  const diff = totalW - sumNow;
  if (diff !== 0) {
    let idx = n - 1;
    let best = -Infinity;
    for (let j = 0; j < n; j++) {
      if (!pinned[j] && widths[j] > best) { best = widths[j]; idx = j; }
    }
    widths[idx] = Math.max(MIN_COL, widths[idx] + diff);
  }
  return widths;
}

export default function GridCanvas({
  lists,
  listsLoading = false,
  columnCount,
  columnWeights,
  onUpdate,
  onClose,
  onArchive,
  onUnarchive,
  onDelete,
  onFocus,
  onExpand,
  // Walter 2026-06-09: lets the empty-board first-step card add the
  // user's first list directly (grid mode is the default for new boards,
  // so this card — not BoardPage's free-mode one — is what new users see).
  onAddList,
  focusedId,
  onSaveStatus,
  registerPendingFlush,
  boards,
  currentBoardId,
  onMoveList,
  // Bumped when the user drops a tile past the right edge — creates a
  // new column. BoardPage wires this to onUpdateBoard(board.id, { gridColumnCount }).
  onColumnCountChange,
  // Called when the user clicks the "remove column" affordance inside
  // an empty column. Receives the column index. Optional — when not
  // provided, the affordance isn't rendered.
  onRemoveColumn,
  // Called after a column-resize drag with the new weights array.
  onColumnWeightsChange,
  jumpTarget,
  onJumpHandled,
  // Reminder feature — passed through to ListPanel so the cross-list
  // capture flow works in grid mode. Omitting these here is what kept
  // reminders broken on desktop (mobile uses MobileBoard, which had its
  // own pass-through that already worked).
  userPrefs,
  updateUserPrefs,
  createReminder,
  reminders,
  updateReminder,
  deleteReminder,
  pushToast,
  // Walter 2026-05-06 round 5: passed through to ListPanel's
  // designated-reminder-list count band so clicking it opens the
  // structured reminder view (agenda).
  onOpenAgenda,
  onOpenReminderEditor,
  onRequestNewReminder,
  // Sharing/collab plumbing — same pattern as the reminders props
  // above. Without these, ListPanel's edit-lock hook short-circuits
  // on every grid-mode tile because currentUid arrives as undefined.
  currentUid,
  currentUserName,
  boardOwnerUid,
  boardEditors,
  boardViewers,
  onShareList,
  onPeekShare,
  // Walter 2026-05-11: forwarded to ListPanel so the "place in
  // board…" menu item works inside grid mode too.
  onPlaceList,
  isGuestOnBoard = false,
  isGuest = false,
  onRequestSignIn,
  // Walter 2026-05-13: organize trigger forwarded to each tile.
  onTriggerOrganize,
}) {
  const wrapRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });

  // ── Drag state ─────────────────────────────────────────────
  // Reorder drag. `dragId` flips to non-null only after the cursor moves
  // past DRAG_THRESHOLD — a plain click never triggers a drag render, so
  // "focus this tile" stays clean.
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  // Cursor position during a reorder drag, used to render the ghost tile.
  const [ghostPos, setGhostPos] = useState(null);
  // Override for column widths during a column-resize drag. Array of
  // pixel widths; when set, overrides the weights-derived layout.
  const [columnWidthOverride, setColumnWidthOverride] = useState(null);
  // Override for tile heights within one column during a tile-resize drag.
  // Shape: { columnIndex, heights: number[] } | null
  const [tileHeightOverride, setTileHeightOverride] = useState(null);

  // Refs mirror the latest closure versions of the hit-test and drop
  // handlers. The mousedown-captured mousemove/mouseup listeners run
  // across state changes and would otherwise stale-close over them.
  const computeDropTargetRef = useRef(null);
  const applyDropRef = useRef(null);

  // Drag edge auto-scroll (Walter 2026-07-04). Created once: while a reorder
  // drag is active, if the cursor nears an edge of the canvas (wrapRef) it
  // scrolls that way so you can drop below/above the current fold. After each
  // scroll step we re-hit-test the drop target at the last cursor position so
  // the drop indicator tracks the scrolled content.
  const lastPointerRef = useRef(null);
  const autoScrollRef = useRef(null);
  if (!autoScrollRef.current) {
    autoScrollRef.current = createDragAutoScroll({
      getContainer: () => wrapRef.current,
      onScroll: () => {
        const p = lastPointerRef.current;
        if (p) setDropTarget(computeDropTargetRef.current(p.x, p.y));
      },
    });
  }

  // Track the canvas container's own dimensions rather than window.inner*
  // — the canvas sits inside a flex layout and its size can change even
  // when the window doesn't (e.g. sidebar open/close).
  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const obs = new ResizeObserver(() => {
      setViewport({ w: el.clientWidth, h: el.clientHeight });
    });
    obs.observe(el);
    setViewport({ w: el.clientWidth, h: el.clientHeight });
    return () => obs.disconnect();
  }, []);

  // Bucket tiles by gridCol; within each column sort by gridRow. Tiles
  // missing a column index fall into column 0 so grid mode stays sane
  // even if the seed step didn't fully write (e.g. network blip).
  const columns = useMemo(() => {
    const n = Math.max(1, columnCount || 1);
    const cols = Array.from({ length: n }, () => []);
    for (const l of lists) {
      const c = Math.max(0, Math.min(n - 1, l.gridCol ?? 0));
      cols[c].push(l);
    }
    for (const col of cols) col.sort(byGridRow);
    return cols;
  }, [lists, columnCount]);

  const n = columns.length;
  // The viewport has to accommodate n column widths + (n-1) inter-gaps
  // + 2*OUTER_PAD (outer frame). Columns fit in whatever's left.
  const gapsHorizontal = Math.max(0, n - 1) * GAP;
  const targetTotalW = viewport.w > 0
    ? Math.max(0, viewport.w - gapsHorizontal - 2 * OUTER_PAD)
    : n * MIN_COL;
  const weightDerivedWidths = computeColumnWidths(columnWeights, n, targetTotalW);
  const columnWidths = columnWidthOverride && columnWidthOverride.length === n
    ? columnWidthOverride
    : weightDerivedWidths;
  const tileWidthSum = columnWidths.reduce((a, b) => a + b, 0);
  // totalW = full width of the grid-content element including its own
  // outer padding. Used for horizontal overflow and for positioning the
  // right-edge new-column drop zone.
  const totalW = tileWidthSum + gapsHorizontal + 2 * OUTER_PAD;
  const horizontalOverflow = totalW > viewport.w;
  // Cumulative x positions of each column's LEFT edge — measured from
  // the grid-content element's padding-box (i.e. inside its border,
  // including its own padding). Column 0 starts at x=OUTER_PAD so it
  // sits inside the outer frame.
  const columnOffsets = useMemo(() => {
    const offs = [OUTER_PAD];
    for (let i = 0; i < columnWidths.length - 1; i++) {
      offs.push(offs[i] + columnWidths[i] + GAP);
    }
    return offs;
  }, [columnWidths]);

  // Per-column required heights. Space for tiles at MIN_TILE_H plus the
  // (count-1) gaps between them. Floored at the usable viewport height
  // (viewport minus the outer padding top+bottom).
  const usableViewportH = Math.max(0, viewport.h - 2 * OUTER_PAD);
  const columnHeights = useMemo(() => columns.map(col => {
    const count = col.length;
    const gapsV = Math.max(0, count - 1) * GAP;
    return Math.max(usableViewportH, count * MIN_TILE_H + gapsV);
  }), [columns, usableViewportH]);
  const innerCanvasHeight = columnHeights.length > 0
    ? Math.max(usableViewportH, ...columnHeights)
    : usableViewportH;
  // Full element height including outer frame padding.
  const canvasHeight = innerCanvasHeight + 2 * OUTER_PAD;
  const verticalOverflow = canvasHeight > viewport.h;

  // ── Hit test: cursor → drop target ─────────────────────────
  const computeDropTarget = useCallback((clientX, clientY) => {
    const wrap = wrapRef.current;
    if (!wrap) return null;
    const wrapRect = wrap.getBoundingClientRect();
    const wrapScrollLeft = wrap.scrollLeft;
    // New-column zone: anywhere past the rightmost column's trailing
    // edge (in the right outer padding and beyond). The right outer
    // padding is a natural landing zone for "append new column".
    const contentX = clientX - wrapRect.left + wrapScrollLeft;
    // New leftmost column zone — anywhere in the left outer padding.
    // Dragging a tile past the leftmost column's leading edge prepends
    // a new column. Mirror of the rightmost newCol behavior.
    if (contentX < OUTER_PAD) {
      return { kind: 'newColLeft' };
    }
    const lastColRightEdge = OUTER_PAD + tileWidthSum + gapsHorizontal;
    if (contentX >= lastColRightEdge) {
      return { kind: 'newCol' };
    }
    // Between-column zones: narrow band centered on each inter-column
    // gap. Triggers when the cursor is clearly in the gap, mirroring
    // the newCol behavior at the right edge. contentX is already in
    // scope from the newCol check above.
    for (let i = 0; i < columns.length - 1; i++) {
      const boundary = columnOffsets[i + 1];
      if (Math.abs(contentX - boundary) <= NEW_COL_ZONE / 2) {
        return { kind: 'newColBetween', leftColIdx: i };
      }
    }
    // Element-based column lookup — handles per-column vertical scroll.
    let el = document.elementFromPoint(clientX, clientY);
    while (el && el !== wrap && !(el.dataset && el.dataset.gridCol != null)) {
      el = el.parentElement;
    }
    let col;
    if (el && el.dataset && el.dataset.gridCol != null) {
      col = parseInt(el.dataset.gridCol, 10);
    } else {
      // Fallback: derive column from x-coord against offsets. contentX is
      // already in scope from the between-column zone check above.
      for (let i = 0; i < columnWidths.length; i++) {
        const left = columnOffsets[i];
        const right = left + columnWidths[i];
        if (contentX >= left && contentX < right) { col = i; break; }
      }
    }
    if (!Number.isInteger(col) || col < 0 || col >= columns.length) return null;
    const colTiles = columns[col];
    if (colTiles.length === 0) return { kind: 'row', col, row: 0 };
    const colEl = el && el.dataset && el.dataset.gridCol != null ? el : null;
    const colRect = colEl ? colEl.getBoundingClientRect() : {
      // Grid content has OUTER_PAD on top, so columns start OUTER_PAD
      // below the wrap's top edge. Fallback only reached when
      // elementFromPoint missed — rare but keep correct.
      top: wrapRect.top + OUTER_PAD,
    };
    // No per-column scroll anymore (wrap scrolls instead). getBoundingClientRect
    // already accounts for any wrap scrollTop, so `clientY - colRect.top` is
    // the cursor's content-y within the column.
    const relativeY = clientY - colRect.top;
    // The column's total content (tiles + gaps) sums to columnHeight. Tile
    // pixels alone sum to columnHeight - gaps*(n-1). Pass the tile total
    // so proportional heights reflect only the visible tile area.
    const colCount = colTiles.length;
    const colH = columnHeights[col] || viewport.h;
    const colTilesUsableH = Math.max(colCount * MIN_TILE_H, colH - Math.max(0, colCount - 1) * GAP);
    const heights = computeProportionalHeights(colTiles, colTilesUsableH);
    let y = 0;
    for (let i = 0; i < colTiles.length; i++) {
      const bottom = y + heights[i];
      if (relativeY < y + heights[i] / 2) return { kind: 'row', col, row: i };
      if (relativeY < bottom) return { kind: 'row', col, row: i + 1 };
      y = bottom + GAP;
    }
    return { kind: 'row', col, row: colTiles.length };
  }, [columns, columnHeights, columnOffsets, columnWidths, totalW, viewport.h]);

  // Apply a drop: rebuild the column structure with the tile moved,
  // prune any column that's now empty, and write only the diffs. When
  // the column count or weights change, emit via onColumnCountChange
  // (which updates both atomically).
  const applyDrop = useCallback((tileId, target) => {
    const work = columns.map(c => c.map(t => t.id));
    // Working weights, synced with columns length (pad/truncate stale
    // arrays defensively).
    let workWeights;
    if (columnWeights && columnWeights.length > 0) {
      workWeights = columnWeights.slice(0, columns.length);
      while (workWeights.length < columns.length) workWeights.push(1);
    } else {
      workWeights = new Array(columns.length).fill(1);
    }

    // 1. Remove tile from its current slot.
    let sourceCol = -1, sourceRow = -1;
    for (let c = 0; c < work.length; c++) {
      const idx = work[c].indexOf(tileId);
      if (idx >= 0) { sourceCol = c; sourceRow = idx; work[c].splice(idx, 1); break; }
    }
    if (sourceCol < 0) return;

    // 2. Insert tile at target. Track if we added a column (so we know
    //    whether to splice a weight too AND whether to shift the
    //    source-col index when computing the prune below).
    //
    // Walter 2026-05-26 — every newCol* branch first tries to absorb
    // into an EXISTING empty column at that position before creating
    // a new one. Reason: on a board with user-added empty columns
    // (e.g. a 3-column blank board where the user has only filled
    // col 0), a drag near the "between-columns" band would trigger
    // newColBetween and INSERT a 4th column — even though col 1 was
    // sitting right there empty waiting for content. Users read that
    // as "my 3-col board grew to 4 cols for no reason." Absorption
    // preserves the existing column count and the user's mental
    // model of how many columns the board has.
    let insertedColumn = false;
    if (target.kind === 'newCol') {
      // Right edge: absorb into the rightmost column if it's empty,
      // otherwise append a new column.
      if (work.length > 0 && work[work.length - 1].length === 0) {
        work[work.length - 1].push(tileId);
      } else {
        work.push([tileId]);
        workWeights.push(1);
        insertedColumn = true;
      }
    } else if (target.kind === 'newColLeft') {
      // Left edge: absorb into the leftmost column if it's empty,
      // otherwise prepend.
      if (work.length > 0 && work[0].length === 0) {
        work[0].push(tileId);
      } else {
        work.unshift([tileId]);
        workWeights.unshift(1);
        insertedColumn = true;
      }
    } else if (target.kind === 'newColBetween') {
      const at = Math.min(Math.max(0, target.leftColIdx + 1), work.length);
      // Between: absorb into the empty column at the insert position
      // OR (if that one is full) into the empty column just left of
      // it (the boundary band is the gap between those two; either
      // empty neighbour is a fine landing spot).
      if (at < work.length && work[at].length === 0) {
        work[at].push(tileId);
      } else if (at > 0 && work[at - 1].length === 0) {
        work[at - 1].push(tileId);
      } else {
        work.splice(at, 0, [tileId]);
        workWeights.splice(at, 0, 1);
        insertedColumn = true;
      }
    } else if (target.kind === 'row') {
      let { col, row } = target;
      if (col < 0 || col >= work.length) return;
      const colArr = work[col];
      const clampedRow = Math.min(Math.max(0, row), colArr.length);
      if (col === sourceCol) {
        // clampedRow is computed against the post-removal array, so
        // it is the final index after splice — compare directly
        // against sourceRow to detect a no-op.
        if (clampedRow === sourceRow) return;
      }
      colArr.splice(clampedRow, 0, tileId);
    }

    // 3. Prune only the SOURCE column if dragging the last tile out
    //    emptied it. Walter 2026-05-26: previously this iterated all
    //    columns and pruned ANY empty one, which silently destroyed
    //    pre-existing empty columns the user had deliberately added
    //    (e.g. a 3-column board where lists only happen to live in
    //    cols 0 and 1 — column 2 disappeared after the first drag).
    //    The intended semantic is "drag the last tile out → that
    //    column collapses", scoped to the column the drag came from.
    //    Other empty columns are user-state, not garbage to clean up.
    //
    //    Source column may have shifted index after step 2's insert:
    //    a newColLeft / newColBetween that inserts at or before
    //    sourceCol bumps it one slot to the right. BUT — only when
    //    insertedColumn is actually true. If step 2 absorbed the
    //    tile into an existing empty column instead of inserting a
    //    new one, the array length is unchanged and sourceCol's
    //    index doesn't move.
    let effectiveSourceCol = sourceCol;
    if (insertedColumn) {
      if (target.kind === 'newColLeft') {
        effectiveSourceCol += 1;
      } else if (target.kind === 'newColBetween'
          && (target.leftColIdx + 1) <= sourceCol) {
        effectiveSourceCol += 1;
      }
    }
    let prunedAny = false;
    if (work.length > 1
        && effectiveSourceCol >= 0
        && effectiveSourceCol < work.length
        && work[effectiveSourceCol].length === 0) {
      work.splice(effectiveSourceCol, 1);
      workWeights.splice(effectiveSourceCol, 1);
      prunedAny = true;
    }

    // 4. Diff against original positions; emit writes for anything that
    //    actually changed row/col. Tiles in untouched columns with no
    //    re-indexing get nothing, which keeps the write count minimal.
    const origPositions = new Map();
    columns.forEach((col, ci) => {
      col.forEach((t, ri) => origPositions.set(t.id, { ci, ri }));
    });
    const writes = [];
    work.forEach((col, ci) => {
      col.forEach((id, ri) => {
        const orig = origPositions.get(id);
        if (!orig || orig.ci !== ci || orig.ri !== ri) {
          writes.push({ id, gridCol: ci, gridRow: ri });
        }
      });
    });
    for (const w of writes) onUpdate(w.id, { gridCol: w.gridCol, gridRow: w.gridRow });

    // 5. Emit column-level changes. If the count changed (add or prune),
    //    use onColumnCountChange with weights for an atomic update. If
    //    count is the same but weights changed (add+prune netted zero,
    //    or just a prune with same count), emit weights alone.
    const countChanged = work.length !== columns.length;
    const weightsChanged = insertedColumn || prunedAny;
    if (countChanged && onColumnCountChange) {
      onColumnCountChange(work.length, workWeights);
    } else if (weightsChanged && onColumnWeightsChange) {
      onColumnWeightsChange(workWeights);
    }
  }, [columns, columnWeights, onUpdate, onColumnCountChange, onColumnWeightsChange]);

  computeDropTargetRef.current = computeDropTarget;
  applyDropRef.current = applyDrop;

  // Look up the original (pre-drag) title to show in the ghost. Avoids
  // a dangling reference if the list gets re-rendered mid-drag.
  const draggedList = dragId != null ? lists.find(l => l.id === dragId) : null;
  const ghostTitle = draggedList
    ? (draggedList.title?.trim() || (draggedList.content?.split('\n').find(l => l.trim()) || '(empty)'))
    : '';

  // ── Reorder drag: tile header → new slot ────────────────────
  const handleGridHeaderMouseDown = useCallback((listId, e) => {
    if (e.target.tagName === 'BUTTON') return;
    if (e.detail === 2) return; // double-click for rename
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    let dragged = false;

    const onMove = (ev) => {
      if (!dragged) {
        if (Math.abs(ev.clientX - startX) > DRAG_THRESHOLD ||
            Math.abs(ev.clientY - startY) > DRAG_THRESHOLD) {
          dragged = true;
          setDragId(listId);
        }
      }
      if (dragged) {
        lastPointerRef.current = { x: ev.clientX, y: ev.clientY };
        autoScrollRef.current.update(ev.clientX, ev.clientY);
        setGhostPos({ x: ev.clientX, y: ev.clientY });
        setDropTarget(computeDropTargetRef.current(ev.clientX, ev.clientY));
      }
    };
    const onUp = (ev) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      autoScrollRef.current.stop();
      if (dragged) {
        const target = computeDropTargetRef.current(ev.clientX, ev.clientY);
        if (target) applyDropRef.current(listId, target);
      }
      setDragId(null);
      setDropTarget(null);
      setGhostPos(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // ── Column resize: drag a seam between two columns ─────────
  const handleColumnResizeMouseDown = useCallback((leftColIdx, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidths = columnWidths.slice();
    // Local closure var for the final override so the commit side-effect
    // on mouseup can run outside of setState (which would double-invoke
    // in React strict mode).
    let latest = null;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const next = startWidths.slice();
      const maxRight = startWidths[leftColIdx + 1] - MIN_COL;
      const maxLeft = startWidths[leftColIdx] - MIN_COL;
      const clamped = Math.max(-maxLeft, Math.min(maxRight, dx));
      next[leftColIdx] = startWidths[leftColIdx] + clamped;
      next[leftColIdx + 1] = startWidths[leftColIdx + 1] - clamped;
      latest = next;
      setColumnWidthOverride(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (latest && onColumnWeightsChange) {
        onColumnWeightsChange(latest.slice());
      }
      setColumnWidthOverride(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [columnWidths, onColumnWeightsChange]);

  // ── Tile resize: drag a seam between two tiles in a column ─
  const handleTileResizeMouseDown = useCallback((columnIndex, topRowIdx, e) => {
    e.preventDefault();
    e.stopPropagation();
    const col = columns[columnIndex];
    if (!col || topRowIdx >= col.length - 1) return;
    const colH = columnHeights[columnIndex] || viewport.h;
    // Usable height for tiles (excludes inter-tile gaps).
    const colUsableH = Math.max(col.length * MIN_TILE_H, colH - Math.max(0, col.length - 1) * GAP);
    const currentHeights = computeProportionalHeights(col, colUsableH);
    const startY = e.clientY;
    const startH = currentHeights.slice();
    // Snapshot original h values so we can update two tiles on commit.
    const sumH = col.reduce((s, t) => s + Math.max(60, t.h || 320), 0);
    const scale = sumH / colUsableH; // pixels → h-units

    // Same closure-var pattern as column resize: commit outside setState.
    let latest = null;

    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      const next = startH.slice();
      const maxDown = startH[topRowIdx + 1] - MIN_TILE_H;
      const maxUp = startH[topRowIdx] - MIN_TILE_H;
      const clamped = Math.max(-maxUp, Math.min(maxDown, dy));
      next[topRowIdx] = startH[topRowIdx] + clamped;
      next[topRowIdx + 1] = startH[topRowIdx + 1] - clamped;
      latest = next;
      setTileHeightOverride({ columnIndex, heights: next });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (latest) {
        const origH0 = startH[topRowIdx];
        const deltaPx = latest[topRowIdx] - origH0;
        const deltaH = deltaPx * scale;
        const t0 = col[topRowIdx];
        const t1 = col[topRowIdx + 1];
        const oldH0 = Math.max(60, t0.h || 320);
        const oldH1 = Math.max(60, t1.h || 320);
        onUpdate(t0.id, { h: Math.max(60, Math.round(oldH0 + deltaH)) });
        onUpdate(t1.id, { h: Math.max(60, Math.round(oldH1 - deltaH)) });
      }
      setTileHeightOverride(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [columns, viewport.h, onUpdate]);

  // ── Drop indicator geometry ─────────────────────────────────
  const dropIndicatorStyle = (() => {
    if (!dropTarget) return null;
    if (dropTarget.kind === 'newCol') {
      // Render the indicator in the middle of the right outer-padding
      // strip. innerW + OUTER_PAD is the right edge of the last column
      // in padding-box coords; + OUTER_PAD/2 puts the indicator centered
      // in the remaining outer frame.
      const innerW = tileWidthSum + gapsHorizontal;
      return {
        position: 'absolute',
        left: (OUTER_PAD + innerW + OUTER_PAD / 2 - 1.5) + 'px',
        top: OUTER_PAD + 'px',
        width: '3px',
        height: innerCanvasHeight + 'px',
        background: 'var(--accent)',
        pointerEvents: 'none',
        zIndex: 30,
      };
    }
    if (dropTarget.kind === 'newColLeft') {
      // Mirror of newCol's indicator, but in the LEFT outer-padding
      // strip. Centered in the OUTER_PAD frame to the left of col 0.
      return {
        position: 'absolute',
        left: (OUTER_PAD / 2 - 1.5) + 'px',
        top: OUTER_PAD + 'px',
        width: '3px',
        height: innerCanvasHeight + 'px',
        background: 'var(--accent)',
        pointerEvents: 'none',
        zIndex: 30,
      };
    }
    if (dropTarget.kind === 'newColBetween') {
      // Middle of the gap between two columns. columnOffsets[i]+cw[i] is
      // the right edge of the LEFT column in padding-box coords.
      const i = dropTarget.leftColIdx;
      const rightEdgeOfLeftCol = columnOffsets[i] + columnWidths[i];
      return {
        position: 'absolute',
        left: (rightEdgeOfLeftCol + GAP / 2 - 1.5) + 'px',
        top: OUTER_PAD + 'px',
        width: '3px',
        height: innerCanvasHeight + 'px',
        background: 'var(--accent)',
        pointerEvents: 'none',
        zIndex: 30,
      };
    }
    const { col, row } = dropTarget;
    const colTiles = columns[col] || [];
    const colCount = colTiles.length;
    const colH = columnHeights[col] || viewport.h;

    // Walter 2026-05-26 — when the target column is empty, render the
    // indicator as a FRAME around the whole column instead of a 3px
    // line at the top. The thin-line treatment was reading as
    // "insert above the top tile" even though there were no tiles —
    // users couldn't tell the tile would land INSIDE the column vs
    // get prepended to it. A full-column frame says "drop here"
    // unambiguously. Non-empty columns keep the thin-line indicator
    // because the user IS inserting at a specific row position; the
    // line shows exactly where the new tile slots in.
    if (colCount === 0) {
      return {
        position: 'absolute',
        left: columnOffsets[col] + 'px',
        top: OUTER_PAD + 'px',
        width: columnWidths[col] + 'px',
        height: colH + 'px',
        // Translucent accent fill + accent border. Translucent so any
        // existing column chrome (none today, but defensive) reads
        // through, and the frame is clearly an overlay.
        background: 'var(--accent-wash, rgba(232, 98, 44, 0.08))',
        border: '2px solid var(--accent)',
        borderRadius: 'var(--radius)',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        zIndex: 30,
      };
    }

    const colUsableH = Math.max(colCount * MIN_TILE_H, colH - Math.max(0, colCount - 1) * GAP);
    const heights = computeProportionalHeights(colTiles, colUsableH);
    // Sum of tile pixel heights (no gaps). Gaps go between them.
    const tilesSumH = heights.reduce((a, b) => a + b, 0);
    // Compute the top-Y of the 3px-tall drop indicator so it always sits
    // fully inside the column: pinned against the top for row=0,
    // centered in the gap for row=k (0<k<count), pinned against the
    // bottom for row=count.
    let topY;
    if (row <= 0) {
      topY = 0;
    } else if (row >= colCount) {
      topY = tilesSumH + Math.max(0, colCount - 1) * GAP - 3;
    } else {
      // End of tile row-1, plus half the gap, centered around the 3px
      // indicator (so (GAP - 3) / 2 above the gap's midpoint).
      let y = 0;
      for (let i = 0; i < row; i++) {
        y += heights[i];
        if (i < row - 1) y += GAP;
      }
      topY = y + (GAP - 3) / 2;
    }
    return {
      position: 'absolute',
      left: columnOffsets[col] + 'px',
      // Column tiles start at y=OUTER_PAD in padding-box coords (grid
      // content has top padding before the column's content).
      top: (OUTER_PAD + topY) + 'px',
      width: columnWidths[col] + 'px',
      height: '3px',
      background: 'var(--accent)',
      pointerEvents: 'none',
      zIndex: 30,
    };
  })();

  return (
    <div
      ref={wrapRef}
      style={{
        flex: 1, position: 'relative',
        overflowX: horizontalOverflow ? 'auto' : 'hidden',
        overflowY: verticalOverflow ? 'auto' : 'hidden',
        minWidth: 0,
        isolation: 'isolate',
        cursor: dragId != null ? 'grabbing' : 'default',
      }}
    >
      {viewport.w > 0 && (
        <div
          className="dot-grid"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: GAP + 'px',
            padding: OUTER_PAD + 'px',
            width: totalW + 'px',
            // totalW and canvasHeight are the full element size INCLUDING
            // the OUTER_PAD on all sides. Box-sizing: border-box (set
            // globally) keeps padding inside those dimensions, so the
            // flex-content area is innerW × innerCanvasHeight — exactly
            // what the columns+gaps sum to. Canvas bg fills the padding
            // area, providing the outer frame each edge tile needs for
            // its focus ring. The .dot-grid class paints var(--bg) plus
            // a subtle dot pattern so the canvas has visible surface grain.
            height: canvasHeight + 'px',
            position: 'relative',
          }}
        >
          {columns.map((col, ci) => {
            const overrideForCol = tileHeightOverride && tileHeightOverride.columnIndex === ci
              ? tileHeightOverride.heights
              : null;
            return (
              <GridColumn
                key={ci}
                columnIndex={ci}
                tiles={col}
                columnWidth={columnWidths[ci]}
                isLast={ci === n - 1}
                columnHeight={columnHeights[ci]}
                draggingId={dragId}
                heightOverride={overrideForCol}
                onGridHeaderMouseDown={handleGridHeaderMouseDown}
                onTileResizeMouseDown={handleTileResizeMouseDown}
                onUpdate={onUpdate}
                onClose={onClose}
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                onDelete={onDelete}
                onFocus={onFocus}
                onExpand={onExpand}
                focusedId={focusedId}
                onSaveStatus={onSaveStatus}
                registerPendingFlush={registerPendingFlush}
                boards={boards}
                currentBoardId={currentBoardId}
                onMoveList={onMoveList}
                jumpTarget={jumpTarget}
                onJumpHandled={onJumpHandled}
                userPrefs={userPrefs}
                updateUserPrefs={updateUserPrefs}
                createReminder={createReminder}
                reminders={reminders}
                updateReminder={updateReminder}
                deleteReminder={deleteReminder}
                pushToast={pushToast}
                onOpenAgenda={onOpenAgenda}
                onOpenReminderEditor={onOpenReminderEditor}
                onRequestNewReminder={onRequestNewReminder}
                // Sharing/collab passthrough — GridColumn forwards to ListPanel
                currentUid={currentUid}
                currentUserName={currentUserName}
                boardOwnerUid={boardOwnerUid}
                boardEditors={boardEditors}
                boardViewers={boardViewers}
                onShareList={onShareList}
                onPeekShare={onPeekShare}
                onPlaceList={onPlaceList}
                isGuestOnBoard={isGuestOnBoard}
                // Walter 2026-05-28: the outer GridCanvas destructures
                // isGuest / onRequestSignIn but was NOT forwarding them
                // to this inner column renderer — so the per-tile
                // ListPanel always saw isGuest=false and the guest
                // reminder-list gray text + inline band never showed.
                isGuest={isGuest}
                onRequestSignIn={onRequestSignIn}
                onRemoveColumn={onRemoveColumn}
                canRemove={n > 1}
                onTriggerOrganize={onTriggerOrganize}
              />
            );
          })}

          {/* Column-resize seams — one per gap between columns,
              positioned at the center of each gap. columnOffsets[i+1] is
              column i+1's LEFT edge, so gap center = that minus GAP/2.
              Seam spans only the inner canvas area (not the outer
              padding) so the draggable line ends at column top/bottom. */}
          {Array.from({ length: Math.max(0, n - 1) }, (_, i) => (
            <Seam
              key={`cseam-${i}`}
              orientation="vertical"
              x={columnOffsets[i + 1] - GAP / 2}
              y={OUTER_PAD}
              height={innerCanvasHeight}
              onMouseDown={(e) => handleColumnResizeMouseDown(i, e)}
            />
          ))}

          {/* Drop indicator — thin accent-colored line at the drop target. */}
          {dropIndicatorStyle && <div style={dropIndicatorStyle} />}
        </div>
      )}

      {/* Ghost tile following the cursor during reorder drag. Fixed
          positioning so it's relative to the viewport, not any scroll
          container. pointerEvents:none so it doesn't block hit-testing. */}
      {dragId != null && ghostPos && (
        <div style={{
          position: 'fixed',
          left: (ghostPos.x + 12) + 'px',
          top: (ghostPos.y + 12) + 'px',
          pointerEvents: 'none',
          zIndex: 9999,
          background: 'var(--surface)',
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-tile-drag)',
          padding: '8px 12px',
          maxWidth: '260px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          color: 'var(--text)',
          opacity: 0.92,
        }}>
          {ghostTitle}
        </div>
      )}

      {/* Empty-board hint — only when there are truly zero tiles. */}
      {lists.length === 0 && !listsLoading && (
        // Walter 2026-06-16: !listsLoading gate so the empty-board card
        // doesn't flash during the mount→first-snapshot load window on every
        // reload (the s2a float made the flicker obvious).
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '9vh', userSelect: 'none', pointerEvents: 'none' }}>
          {/* Walter 2026-06-15 (s2a): on short / small screens the card
              crowded the empty-column "× remove column" buttons (which sit
              at top:50% of each column). Lift it higher (9vh) for more gap
              below, and give it a heavy elevation shadow so it reads as a
              card floating above the canvas rather than bunched into it. */}
          <div style={{ pointerEvents: 'auto', maxWidth: 380, width: 'calc(100% - 32px)', background: 'var(--surface)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-modal-heavy)', padding: '20px 22px', textAlign: 'center', fontFamily: 'var(--mono)' }}>
            <div style={{ fontSize: 'calc(14px * var(--ui-scale, 1))', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>This is your board. Let's add something.</div>
            <div style={{ fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--text-dim)', lineHeight: 1.55, marginBottom: '14px' }}>A board holds your lists and notes. Add your first list to get going, or press <strong>{SHORTCUT.newList}</strong> any time.</div>
            <button type="button" onClick={() => onAddList && onAddList()} style={{ background: 'var(--accent)', color: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)', fontSize: 'calc(12px * var(--ui-scale, 1))', fontWeight: 600, padding: '9px 18px', cursor: 'pointer' }} onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-dim)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'var(--accent)'; }}>+ add your first list</button>
            <div style={{ marginTop: '9px', fontSize: 'calc(10px * var(--ui-scale, 1))', color: 'var(--text-dimmer)', lineHeight: 1.5 }}>to add more lists, click <strong>+ new</strong> up top or just press <strong>{SHORTCUT.newList}</strong></div>
            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border)', fontSize: 'calc(10px * var(--ui-scale, 1))', color: 'var(--text-dimmer)', lineHeight: 1.5 }}>Have more than one board? Switch between them from the <strong>boards</strong> menu next to the logo, top-left.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// Draggable resize seam. Thin hairline at rest, brighter/thicker on
// hover, accent-colored. Used for both column-width (vertical) and
// tile-height (horizontal) resize handles.
function Seam({ orientation, x, y, height, onMouseDown }) {
  const [hover, setHover] = useState(false);
  const vertical = orientation === 'vertical';
  const hitZone = vertical
    ? {
        position: 'absolute',
        left: (x - SEAM_HIT / 2) + 'px',
        top: (y != null ? y : 0) + 'px',
        width: SEAM_HIT + 'px',
        height: (height || '100%') + (typeof height === 'number' ? 'px' : ''),
        cursor: 'col-resize', zIndex: 25,
      }
    : {
        position: 'absolute',
        left: 0, right: 0,
        top: (y - SEAM_HIT / 2) + 'px',
        height: SEAM_HIT + 'px',
        cursor: 'row-resize', zIndex: 5,
      };
  const lineThickness = hover ? 3 : 1;
  // Seams sit in the inter-tile gaps — the canvas bg showing through
  // the gap IS the visible separator at rest, so the seam's own line
  // stays transparent. On hover the line lights up in accent color so
  // the resize affordance is discoverable.
  const lineStyle = vertical
    ? {
        position: 'absolute',
        left: (SEAM_HIT / 2 - lineThickness / 2) + 'px', top: 0,
        width: lineThickness + 'px', height: '100%',
        background: hover ? 'var(--accent)' : 'transparent',
        transition: 'background 0.1s, width 0.1s, left 0.1s',
      }
    : {
        position: 'absolute',
        left: 0, right: 0,
        top: (SEAM_HIT / 2 - lineThickness / 2) + 'px',
        height: lineThickness + 'px',
        background: hover ? 'var(--accent)' : 'transparent',
        transition: 'background 0.1s, height 0.1s, top 0.1s',
      };
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={hitZone}
    >
      <div style={lineStyle} />
    </div>
  );
}

// Proportional height allocation for a column of tiles. Mirrors the old
// auto-arrange algorithm: tall tiles stay taller than short ones relative
// to each other. Any tile whose proportional share would fall below
// MIN_TILE_H gets pinned at MIN_TILE_H; the remaining space is then
// redistributed proportionally among the unpinned tiles. Iterates until
// no more pins are needed. Rounding remainder folds into the tallest
// unpinned tile so the column fills the viewport exactly.
//
// Uses each tile's free-mode `h` as its weight — grid mode never writes
// to `h` outside the tile-resize drag, so this reflects the last sized
// value (or the default 320 for tiles that have never been sized).
export function computeProportionalHeights(tiles, viewportHeight) {
  const count = tiles.length;
  if (count === 0) return [];
  if (count * MIN_TILE_H > viewportHeight) {
    return new Array(count).fill(MIN_TILE_H);
  }
  const origH = tiles.map(t => Math.max(60, t.h || 320));
  const heights = new Array(count).fill(0);
  const pinned = new Array(count).fill(false);
  for (let iter = 0; iter < count + 1; iter++) {
    let pinnedSpace = 0;
    let freeOrig = 0;
    for (let j = 0; j < count; j++) {
      if (pinned[j]) pinnedSpace += MIN_TILE_H;
      else freeOrig += origH[j];
    }
    const freeSpace = viewportHeight - pinnedSpace;
    if (freeOrig <= 0) break;
    let newPin = -1;
    let smallestShare = Infinity;
    for (let j = 0; j < count; j++) {
      if (pinned[j]) continue;
      const share = (origH[j] / freeOrig) * freeSpace;
      if (share < MIN_TILE_H && share < smallestShare) {
        smallestShare = share;
        newPin = j;
      }
    }
    if (newPin === -1) {
      for (let j = 0; j < count; j++) {
        if (!pinned[j]) {
          heights[j] = Math.max(
            MIN_TILE_H,
            Math.floor((origH[j] / freeOrig) * freeSpace),
          );
        } else {
          heights[j] = MIN_TILE_H;
        }
      }
      break;
    }
    pinned[newPin] = true;
  }
  const sumNow = heights.reduce((a, b) => a + b, 0);
  const diff = viewportHeight - sumNow;
  if (diff !== 0) {
    let tallest = 0;
    for (let j = 1; j < count; j++) {
      if (heights[j] > heights[tallest]) tallest = j;
    }
    heights[tallest] = Math.max(MIN_TILE_H, heights[tallest] + diff);
  }
  return heights;
}

// One column in the grid. Renders its tiles plus horizontal resize seams
// between adjacent tiles. Height override (from a tile-resize drag) wins
// over the proportional calculation. Never scrolls internally — the
// canvas as a whole handles vertical overflow at the GridCanvas level.
function GridColumn({
  columnIndex, tiles, columnWidth, isLast, columnHeight,
  draggingId, heightOverride, onGridHeaderMouseDown, onTileResizeMouseDown,
  onUpdate, onClose, onArchive, onUnarchive, onDelete, onFocus, onExpand,
  focusedId, onSaveStatus, registerPendingFlush,
  boards, currentBoardId, onMoveList,
  jumpTarget, onJumpHandled,
  // Reminder feature passthrough — needed for cross-list reminder
  // capture to work in grid mode (the prop chain stops here otherwise).
  userPrefs, updateUserPrefs, createReminder,
  reminders, updateReminder, deleteReminder, pushToast,
  onOpenAgenda,
  onOpenReminderEditor,
  onRequestNewReminder,
  // Remove-column affordance, surfaced when the column has no tiles.
  // canRemove is false when this is the last column on the board (we
  // always keep at least one).
  onRemoveColumn, canRemove,
  // Sharing/collab passthrough — same pattern as the reminder props.
  // GridCanvas → GridColumn → ListPanel. Without these the lock hook
  // gets undefined currentUid and never engages.
  currentUid, currentUserName, boardOwnerUid, boardEditors, boardViewers,
  onShareList,
  onPeekShare,
  // Walter 2026-05-11: "place in board…" forwarded down. Tile knows
  // whether it's a placed-shared-list via `list._sharedFromOwnerUid`.
  onPlaceList,
  isGuestOnBoard = false,
  isGuest = false,
  onRequestSignIn,
  // Walter 2026-05-13: organize trigger forwarded to each tile.
  onTriggerOrganize,
}) {
  const count = tiles.length;
  // Usable height for tiles — subtracts the (count-1) gaps that sit
  // between them. Proportional heights sum to this, not the full column
  // height. The gaps are laid out by CSS gap on the column flex.
  const gapsV = Math.max(0, count - 1) * GAP;
  const usableH = Math.max(count * MIN_TILE_H, columnHeight - gapsV);
  const heights = heightOverride && heightOverride.length === count
    ? heightOverride
    : computeProportionalHeights(tiles, usableH);

  // Cumulative tops for each tile — used to position the inter-tile
  // resize seams. Each step adds a tile's height plus the gap that
  // follows it (except after the last tile).
  const tileTops = [];
  {
    let y = 0;
    for (let i = 0; i < count; i++) {
      tileTops.push(y);
      y += heights[i] + (i < count - 1 ? GAP : 0);
    }
  }

  return (
    <div
      data-grid-col={columnIndex}
      style={{
        width: columnWidth + 'px',
        height: columnHeight + 'px',
        flexShrink: 0,
        // Column has no background — canvas bg shows through any empty
        // space below the last tile, and (more importantly) through the
        // inter-tile gaps. That's what makes each tile read as a
        // standalone card.
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: GAP + 'px',
      }}
    >
      {/* Empty-column affordance — surfaces a small "× remove column"
          button so users can clean up empty columns themselves. We
          only render it when the column has no tiles AND the caller
          confirms removal is allowed (canRemove prevents removing
          the last remaining column). */}
      {count === 0 && onRemoveColumn && canRemove && (
        <button
          onClick={() => onRemoveColumn(columnIndex)}
          title="remove this empty column"
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'transparent',
            border: '1px dashed var(--border-bright)',
            color: 'var(--text-dimmer)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            padding: '6px 12px',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            transition: 'color 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = 'var(--danger, #d04a4a)';
            e.currentTarget.style.borderColor = 'var(--danger, #d04a4a)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'var(--text-dimmer)';
            e.currentTarget.style.borderColor = 'var(--border-bright)';
          }}
        >
          × remove column
        </button>
      )}
      {tiles.map((list, ri) => {
        const isDragging = draggingId === list.id;
        return (
          <div
            key={list.id}
            data-grid-slot={`${columnIndex},${ri}`}
            style={{
              position: 'relative',
              width: '100%',
              height: heights[ri] + 'px',
              // No borderBottom — each tile owns its own border via
              // ListPanel's outerStyle, so stacking tiles get clean
              // single-border separations instead of doubled-up lines.
              opacity: isDragging ? 0.35 : 1,
              transition: 'opacity 0.1s',
            }}
          >
            <ListPanel
              list={list}
              onUpdate={onUpdate}
              onClose={onClose}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onDelete={onDelete}
              onFocus={onFocus}
              onExpand={onExpand}
              isFocused={focusedId === list.id}
              onSaveStatus={onSaveStatus}
              registerPendingFlush={registerPendingFlush}
              boards={boards}
              currentBoardId={currentBoardId}
              onMoveList={onMoveList}
              jumpTarget={jumpTarget?.listId === list.id ? jumpTarget : null}
              onJumpHandled={onJumpHandled}
              inGrid
              onGridHeaderMouseDown={onGridHeaderMouseDown}
              userPrefs={userPrefs}
              updateUserPrefs={updateUserPrefs}
              createReminder={createReminder}
              reminders={reminders}
              updateReminder={updateReminder}
              deleteReminder={deleteReminder}
              pushToast={pushToast}
              onOpenAgenda={onOpenAgenda}
              onOpenReminderEditor={onOpenReminderEditor}
              onRequestNewReminder={onRequestNewReminder}
              // Edit-lock + per-list share plumbing — same shape as the
              // free-mode render in BoardPage.js. Without these the lock
              // hook short-circuits because currentUid is undefined.
              currentUid={currentUid}
              isGuest={isGuest}
              onRequestSignIn={onRequestSignIn}
              currentUserName={currentUserName}
              boardOwnerUid={boardOwnerUid}
              boardEditors={boardEditors}
              boardViewers={boardViewers}
              onPlaceList={onPlaceList}
              isSharedWithMe={isGuestOnBoard || !!list._sharedFromOwnerUid}
              onShareList={onShareList}
              onPeekShare={onPeekShare}
              onTriggerOrganize={onTriggerOrganize ? () => onTriggerOrganize(list.id) : undefined}
            />
          </div>
        );
      })}

      {/* Inter-tile resize seams — one per gap between stacked tiles.
          Positioned at the middle of the gap (tileTops[i+1] lands at the
          NEXT tile's top; subtract GAP/2 for the gap's center). Seams
          themselves are transparent at rest — the gap visual is the
          canvas bg showing through — and only light up on hover. */}
      {count > 1 && tiles.slice(0, -1).map((_, topIdx) => (
        <Seam
          key={`tseam-${topIdx}`}
          orientation="horizontal"
          y={tileTops[topIdx + 1] - GAP / 2}
          onMouseDown={(e) => onTileResizeMouseDown(columnIndex, topIdx, e)}
        />
      ))}
    </div>
  );
}
